export type EnvValue = string | boolean | null | undefined;
export type EnvSource = Record<string, EnvValue>;

export class FrontendConfigError extends Error {
	constructor(message: string) {
		super(message);
		this.name = "FrontendConfigError";
	}
}

export interface FrontendEnvironmentConfig {
	apiUrl: string;
	useProxy: boolean;
	wsApiUrl?: string;
	proxyTarget?: string;
	wsProxyTarget?: string;
	frontendUrl?: string;
	allowedDomains: string[];
	devAllowedHosts: string[];
	devAllowedOrigins: string[];
	debugIngestUrl?: string;
}

function normalizeValue(value: EnvValue): string | undefined {
	if (value === null || value === undefined) {
		return undefined;
	}

	const trimmed = String(value).trim();
	return trimmed ? trimmed : undefined;
}

export function getOptionalEnv(source: EnvSource, key: string): string | undefined {
	return normalizeValue(source[key]);
}

export function getRequiredEnv(source: EnvSource, key: string): string {
	const value = getOptionalEnv(source, key);
	if (!value) {
		throw new FrontendConfigError(
			`Configuração obrigatória ausente: ${key}. Defina essa variável antes de iniciar o frontend.`,
		);
	}
	return value;
}

export function getBooleanEnv(
	source: EnvSource,
	key: string,
	defaultValue = false,
): boolean {
	const value = getOptionalEnv(source, key);
	if (!value) {
		return defaultValue;
	}

	return value.toLowerCase() === "true";
}

export function getCsvEnv(source: EnvSource, key: string): string[] {
	const value = getOptionalEnv(source, key);
	if (!value) {
		return [];
	}

	return value
		.split(",")
		.map((item) => item.trim())
		.filter(Boolean);
}

export function normalizeHostname(input: string): string {
	const value = input.trim();
	if (!value) {
		return "";
	}

	if (/^[a-z]+:\/\//i.test(value)) {
		try {
			return new URL(value).hostname;
		} catch {
			return value;
		}
	}

	try {
		return new URL(`https://${value}`).hostname;
	} catch {
		return value
			.replace(/^[a-z]+:\/\//i, "")
			.split(/[/:?#]/)[0]
			.trim();
	}
}

export function normalizeOriginLike(input: string): string {
	const value = input.trim();
	if (!value) {
		return "";
	}

	const withProtocol = /^[a-z]+:\/\//i.test(value) ? value : `https://${value}`;
	try {
		return new URL(withProtocol).origin;
	} catch {
		return value;
	}
}

export function buildConnectSrcEntriesFromHost(host: string): string[] {
	const normalizedHost = normalizeHostname(host);
	if (!normalizedHost) {
		return [];
	}

	return [
		`http://${normalizedHost}:*`,
		`https://${normalizedHost}:*`,
		`ws://${normalizedHost}:*`,
		`wss://${normalizedHost}:*`,
	];
}

export function buildConnectSrcEntriesFromUrl(url: string): string[] {
	const normalizedOrigin = normalizeOriginLike(url);
	if (!normalizedOrigin) {
		return [];
	}

	try {
		const parsed = new URL(normalizedOrigin);
		const entries = [parsed.origin];
		if (parsed.protocol === "http:") {
			entries.push(`ws://${parsed.host}`);
		}
		if (parsed.protocol === "https:") {
			entries.push(`wss://${parsed.host}`);
		}
		return entries;
	} catch {
		return [normalizedOrigin];
	}
}

export function dedupeEntries(entries: string[]): string[] {
	return [...new Set(entries.filter(Boolean))];
}

export function resolveFrontendEnvironmentConfig(
	source: EnvSource,
): FrontendEnvironmentConfig {
	const useProxy = getBooleanEnv(source, "VITE_USE_PROXY", false);

	return {
		apiUrl: getRequiredEnv(source, "VITE_API_URL"),
		useProxy,
		wsApiUrl: getOptionalEnv(source, "VITE_WS_API_URL"),
		proxyTarget: getOptionalEnv(source, "VITE_PROXY_TARGET"),
		wsProxyTarget: getOptionalEnv(source, "VITE_WS_PROXY_TARGET"),
		frontendUrl: getOptionalEnv(source, "VITE_FRONTEND_URL"),
		allowedDomains: getCsvEnv(source, "VITE_ALLOWED_DOMAINS"),
		devAllowedHosts: getCsvEnv(source, "VITE_DEV_ALLOWED_HOSTS"),
		devAllowedOrigins: getCsvEnv(source, "VITE_DEV_ALLOWED_ORIGINS"),
		debugIngestUrl: getOptionalEnv(source, "VITE_DEBUG_INGEST_URL"),
	};
}

export function resolveViteProxyTargets(config: FrontendEnvironmentConfig): {
	proxyTarget: string;
	wsProxyTarget: string;
} | null {
	if (!config.useProxy) {
		return null;
	}

	if (!config.proxyTarget) {
		throw new FrontendConfigError(
			"VITE_USE_PROXY=true exige VITE_PROXY_TARGET. Defina o target HTTP do proxy antes de iniciar o Vite.",
		);
	}

	return {
		proxyTarget: config.proxyTarget,
		wsProxyTarget: config.wsProxyTarget || config.proxyTarget,
	};
}

export function getSocketConfigurationError(
	config: FrontendEnvironmentConfig,
	currentOrigin?: string,
): string | null {
	if (config.useProxy) {
		if (currentOrigin) {
			return null;
		}

		return "WebSocket via proxy requer window.location.origin disponível no navegador.";
	}

	if (!config.wsApiUrl) {
		return "VITE_USE_PROXY=false exige VITE_WS_API_URL para habilitar o WebSocket.";
	}

	return null;
}

export function resolveFrontendUrl(
	config: FrontendEnvironmentConfig,
	currentOrigin?: string,
): string {
	if (currentOrigin) {
		return currentOrigin;
	}

	if (config.frontendUrl) {
		return config.frontendUrl;
	}

	throw new FrontendConfigError(
		"FRONTEND_URL não pôde ser resolvida fora do navegador. Defina VITE_FRONTEND_URL para contextos sem window.location.",
	);
}
